import React, { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { getActiveStudent } from '@actions/checkout'

import logoDireto from '../assets/sm-direto-cor.svg'
import logoDiretoBranco from '../assets/sm-direto-branco-cor.svg'
import logoSm from '../assets/sm-educacao-horizontal-cor.png'
import logoSmBranco from '../assets/sm-educacao-horizontal-branco.png'
import '../../../css/v2.css'
import { homePath, useV2Official } from '../../../utils/v2Official'

// Chrome das páginas V2 — guia de identidade SM Direto (marketing, set/2026):
// SM Direto assina o cabeçalho; a SM Educação endossa ("UM PORTAL"); fio da marca no
// topo; cabeçalho branco e rodapé grafite. Nas etapas de compra (/v2/steps/*, exceto a
// conclusão) o cabeçalho entra em "modo foco": contexto do voucher, ajuda e sair da
// compra, sem navegação que distraia. Nenhuma regra de negócio vive aqui.
const openAssistant = detail => window.dispatchEvent(new CustomEvent('smd:assistant', { detail: detail || {} }))

export default function V2Layout({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useSelector(state => state.sessionReducer && state.sessionReducer.user)
  const student = useSelector(state => getActiveStudent(state))
  const home = homePath(useV2Official())

  const pathname = location.pathname

  // Chrome V2 no <html>/<body>: o CSS global do V1 usa overflow-x:hidden no body, o que
  // anula position:sticky (cabeçalho e resumo lateral). A classe troca por overflow-x:clip.
  useEffect(() => {
    document.documentElement.classList.add('smd-v2')
    return () => document.documentElement.classList.remove('smd-v2')
  }, [])

  // SPA: cada etapa começa no topo (antes a rolagem da tela anterior era herdada)
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0)
  }, [pathname, location.hash])
  const focus = pathname.startsWith('/v2/steps/') && !pathname.startsWith('/v2/steps/concluido')
  const payment = pathname.startsWith('/v2/steps/resumo') || pathname.startsWith('/v2/steps/pagamento')

  // o user da sessão traz `name` no fluxo de compra e `full_name` na área logada
  const displayName = (user && (user.name || user.full_name)) || ''
  const initials = displayName
    ? displayName.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase()
    : ''

  return (
    <div className="v2-root">
      <div className="v2-brandline" aria-hidden="true" />
      <header className="v2-hd">
        <div className="v2-hd__in">
          <Link to={home} id="v2-nav-home" className="v2-hd__logo" aria-label="SM Direto, página inicial">
            <img src={logoDireto} alt="SM Direto" />
          </Link>

          {focus ? (
            <React.Fragment>
              <span className="v2-hd__spacer" />
              {student && student.voucher ? (
                <span className="v2-hd__context" id="v2-hd-context">
                  Voucher <strong>{student.voucher}</strong>
                  {student.name ? <span className="v2-hd__context-name"> · {student.name}</span> : null}
                </span>
              ) : null}
              {payment ? (
                <span className="v2-hd__secure">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                  Ambiente seguro
                </span>
              ) : null}
              <button type="button" className="v2-hd__link" id="v2-hd-help" onClick={() => openAssistant()}>
                Precisa de ajuda?
              </button>
              {/* o atalho do usuário logado para a conta existe em todo o V2 — no modo foco fica só o avatar */}
              {displayName ? (
                <Link to="/v2/conta/pedidos" id="v2-header-user" className="v2-hd__user" aria-label={`Minha conta, ${displayName}`} title="Minha conta">
                  <span className="v2-hd__avatar">{initials}</span>
                </Link>
              ) : null}
              <button type="button" className="v2-btn v2-btn--neutral v2-btn--sm" id="v2-hd-exit" onClick={() => navigate(home)}>
                Sair da compra
              </button>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <nav className="v2-hd__nav" aria-label="Principal">
                <a href={`${home}#como-funciona`}>Como funciona</a>
                <a href={`${home}#ajuda`}>Dúvidas</a>
                <button type="button" onClick={() => openAssistant()}>Ajuda</button>
              </nav>
              <span className="v2-hd__spacer" />
              <span className="v2-hd__endorse">
                <span>Um portal</span>
                <img src={logoSm} alt="SM Educação" />
              </span>
              {displayName ? (
                <Link to="/v2/conta/pedidos" id="v2-header-user" className="v2-hd__user">
                  <span className="v2-hd__avatar">{initials}</span>
                  <span className="v2-hd__user-name"><small>Olá,</small>{displayName.split(' ')[0]}</span>
                </Link>
              ) : (
                <button
                  id="v2-header-login"
                  type="button"
                  className="v2-btn v2-btn--secondary v2-btn--sm"
                  onClick={() => navigate('/v2/conta/entrar', { state: { returnTo: pathname } })}
                >
                  Entrar
                </button>
              )}
            </React.Fragment>
          )}
        </div>
      </header>

      <main>{children}</main>

      {focus ? (
        <footer className="v2-ft v2-ft--slim">
          <div className="v2-ft__line">
            {/* 10/10: sem 0800 (canal de contato é o chamado — revisão GCA) */}
            <span>Edições SM LTDA · CNPJ 05.699.378/0001-49</span>
            <img src={logoSmBranco} alt="SM Educação" />
          </div>
        </footer>
      ) : (
        <footer className="v2-ft">
          <div className="v2-ft__grid v2-footer-grid">
            <div>
              <img src={logoDiretoBranco} alt="SM Direto" className="v2-ft__logo" />
              <p className="v2-ft__about">
                O canal oficial para as famílias comprarem o material adotado pela escola, direto de quem publica.
              </p>
            </div>
            {/* links institucionais e redes sociais conforme smeducacao.com.br (28/08/2026) */}
            <div className="v2-ft__col">
              <span className="v2-ft__title">Institucional</span>
              <a href="https://www.smeducacao.com.br/sobre-a-sm/" target="_blank" rel="noreferrer">Sobre a SM Educação</a>
              <a href="https://www.smeducacao.com.br/carreiras/" target="_blank" rel="noreferrer">Carreiras</a>
              <a href="https://www.vagas.com.br/edicoessm" target="_blank" rel="noreferrer">Trabalhe conosco</a>
              <a href="https://www.smeducacao.com.br/contato/" target="_blank" rel="noreferrer">Contato</a>
            </div>
            <div className="v2-ft__col">
              <span className="v2-ft__title">Políticas</span>
              <a href="/politica-de-privacidade.html" target="_blank" rel="noreferrer">Política de privacidade</a>
              <a href="/politica-de-compras.html" target="_blank" rel="noreferrer">Política de compras</a>
              <a href="/politica-de-cookies.html" target="_blank" rel="noreferrer">Política de cookies</a>
              <a href="/termos-de-uso.html" target="_blank" rel="noreferrer">Termos de uso</a>
            </div>
            <div className="v2-ft__col">
              <span className="v2-ft__title">Siga a SM</span>
              <a href="https://www.facebook.com/SMEducacao/" target="_blank" rel="noreferrer">Facebook</a>
              <a href="https://www.instagram.com/smeducacao/" target="_blank" rel="noreferrer">Instagram</a>
              <a href="https://www.youtube.com/user/edicoessm" target="_blank" rel="noreferrer">YouTube</a>
              <a href="https://www.linkedin.com/company/sm-educacao/" target="_blank" rel="noreferrer">LinkedIn</a>
            </div>
          </div>
          <div className="v2-ft__line">
            <span>Edições SM LTDA · CNPJ 05.699.378/0001-49 · SM Educação © — Todos os direitos reservados</span>
            <img src={logoSmBranco} alt="SM Educação" />
          </div>
        </footer>
      )}
    </div>
  )
}
